import { format, startOfWeek, isBefore, parseISO, getDay } from 'date-fns';
import { apiClient } from '@/core/api/client';
import {
  PlanningData,
  WeekPlanningResponse,
  PlanningApiResponse,
  DAYS_OF_WEEK,
  DayAvailability,
  PrivateAppointment,
  PrivateAppointmentDTO,
  CreateAppointmentRequest,
  UpdateAppointmentRequest
} from '../types/monEspace.types';

/**
 * Format time slot from API (HH:MM:SS) to UI (HH:MM)
 */
const formatTimeSlot = (time: string): string => {
  if (!time || time === '') return '09:00';
  const parts = time.split(':');
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return time;
};

/**
 * Format time slot from UI (HH:MM) to API (HH:MM:SS)
 */
const formatTimeSlotForAPI = (time: string): string => {
  if (!time || time === '') return '09:00:00';
  if (time.match(/^\d{2}:\d{2}$/)) {
    return `${time}:00`;
  }
  return time;
};

/**
 * Transform API response to frontend PlanningData format
 * Matches expert preferences availability structure
 */
const transformAPIResponseToPlanning = (apiData: any): PlanningData => {
  const planning: Partial<PlanningData> = {};

  DAYS_OF_WEEK.forEach(day => {
    const dayData = apiData[day];
    planning[day] = {
      startingDomicile: 1, // Not used in Mon Espace
      slots: [
        {
          start: dayData?.start ? formatTimeSlot(dayData.start) : '09:00',
          end: dayData?.end ? formatTimeSlot(dayData.end) : '17:00',
          enabled: dayData?.enabled || false
        }
      ],
      workLocation: dayData?.workLocation || 'office',
      isHoliday: dayData?.isHoliday || false
    };
  });

  return planning as PlanningData;
};

/**
 * Transform frontend PlanningData to API format
 * Extracts the first slot from each day's slots array
 */
const transformPlanningForAPI = (planning: Record<string, DayAvailability>): any => {
  const transformed: any = {};

  Object.entries(planning).forEach(([day, dayData]) => {
    if (dayData && dayData.slots && dayData.slots.length > 0) {
      const slot = dayData.slots[0]; // Use first slot
      transformed[day] = {
        start: formatTimeSlotForAPI(slot.start),
        end: formatTimeSlotForAPI(slot.end),
        enabled: slot.enabled,
        workLocation: dayData.workLocation || 'office'
      };
    }
  });

  return transformed;
};

/**
 * Get the Monday of the week for a given date (DST-safe using date-fns)
 */
export const getWeekStart = (date: Date): string => {
  // startOfWeek with weekStartsOn: 1 ensures Monday is the week start
  const monday = startOfWeek(date, { weekStartsOn: 1 });
  return format(monday, 'yyyy-MM-dd');
};

/**
 * Check if a given week start date is in the past (DST-safe)
 */
export const isWeekInPast = (weekStart: string): boolean => {
  const weekStartDate = parseISO(weekStart);
  const currentMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
  return isBefore(weekStartDate, currentMonday);
};

/**
 * Check if a recurring appointment should be displayed on a specific date
 * @param appointment The appointment to check
 * @param date The date (YYYY-MM-DD) to check
 * @returns true if the appointment should be shown on this date
 */
export const shouldShowRecurringAppointment = (appointment: PrivateAppointment, date: string): boolean => {
  if (!appointment.recurring) return false;

  const checkDate = parseISO(date);
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Check if created after the date (using date-fns)
  if (appointment.createdAt) {
    const createdDate = parseISO(appointment.createdAt);
    if (isBefore(checkDate, createdDate)) return false;
  }

  // Check if ended before the date (using date-fns)
  if (appointment.recurrenceEndDate) {
    const endDate = parseISO(appointment.recurrenceEndDate);
    if (isBefore(endDate, checkDate)) return false;
  }

  // Check if date is in exceptions
  if (appointment.exceptions && appointment.exceptions.includes(date)) return false;

  // Check if day of week matches (using date-fns getDay)
  const dayOfWeek = dayNames[getDay(checkDate)];
  if (appointment.dayOfWeek !== dayOfWeek) return false;

  return true;
};

export const monEspaceAPI = {
  /**
   * Get recurrent planning template
   */
  getTemplate: async (): Promise<PlanningData> => {
    try {
      const response = await apiClient.get<PlanningApiResponse>('/api/admin/mon-espace/planning/template');
      if (response.success && response.data) {
        return transformAPIResponseToPlanning(response.data);
      }
      throw new Error(response.message || 'Failed to fetch planning template');
    } catch (error) {
      console.error('Error fetching planning template:', error);
      throw error;
    }
  },

  /**
   * Save recurrent planning template
   */
  saveTemplate: async (planning: Record<string, DayAvailability>): Promise<{ success: boolean }> => {
    try {
      const apiData = transformPlanningForAPI(planning);
      const response = await apiClient.post<PlanningApiResponse>(
        '/api/admin/mon-espace/planning/template',
        { planning: apiData }
      );
      return { success: response.success };
    } catch (error) {
      console.error('Error saving planning template:', error);
      throw error;
    }
  },

  /**
   * Get planning for a specific week (merged template + overrides)
   */
  getWeekPlanning: async (weekStart: string): Promise<WeekPlanningResponse> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: WeekPlanningResponse }>(
        `/api/admin/mon-espace/planning/week/${weekStart}`
      );
      if (response.success && response.data) {
        const transformed = transformAPIResponseToPlanning(response.data.planning);
        return {
          planning: transformed,
          hasOverrides: response.data.hasOverrides,
          isPastWeek: response.data.isPastWeek
        };
      }
      throw new Error('Failed to fetch week planning');
    } catch (error) {
      console.error('Error fetching week planning:', error);
      throw error;
    }
  },

  /**
   * Save override for a specific week
   */
  saveWeekOverride: async (weekStart: string, planning: Record<string, DayAvailability>): Promise<{ success: boolean }> => {
    try {
      const apiData = transformPlanningForAPI(planning);
      const response = await apiClient.post<PlanningApiResponse>(
        `/api/admin/mon-espace/planning/week/${weekStart}`,
        { planning: apiData }
      );
      return { success: response.success };
    } catch (error) {
      console.error('Error saving week override:', error);
      throw error;
    }
  },

  /**
   * Delete override for a specific week (revert to template)
   */
  deleteWeekOverride: async (weekStart: string): Promise<{ success: boolean }> => {
    try {
      const response = await apiClient.delete<PlanningApiResponse>(
        `/api/admin/mon-espace/planning/week/${weekStart}`
      );
      return { success: response.success };
    } catch (error) {
      console.error('Error deleting week override:', error);
      throw error;
    }
  },

  // ===== PRIVATE APPOINTMENTS =====

  /**
   * Transform DTO from backend to frontend format
   */
  transformAppointmentFromDTO: (dto: PrivateAppointmentDTO): PrivateAppointment => {
    return {
      id: dto.id,
      date: dto.date,
      dayOfWeek: dto.dayOfWeek,
      startTime: dto.startTime,
      endTime: dto.endTime,
      title: dto.title,
      startAddress: {
        custom: dto.startAddress || undefined
      },
      endAddress: {
        custom: dto.endAddress || undefined
      },
      travelTimeBefore: dto.travelTimeBefore || 0,
      travelTimeAfter: dto.travelTimeAfter || 0,
      recurring: dto.isRecurring || false,
      recurrencePattern: dto.recurrencePattern as 'weekly' | 'biweekly' | 'monthly' | undefined,
      recurrenceEndDate: dto.recurrenceEndDate || undefined,
      exceptions: dto.exceptions || [],
      overrideDate: dto.overrideDate || undefined,
      overrideParentId: dto.overrideParentId || undefined,
      createdAt: dto.createdAt || undefined,
      isProfessional: dto.isProfessional || false
    };
  },

  /**
   * Transform frontend appointment to API request format
   */
  transformAppointmentToRequest: (appointment: Partial<PrivateAppointment>): CreateAppointmentRequest => {
    const isRecurring = appointment.recurring || false;

    return {
      date: appointment.date!,
      dayOfWeek: appointment.dayOfWeek!,
      startTime: appointment.startTime!,
      endTime: appointment.endTime!,
      title: appointment.title!,
      travelTimeBefore: appointment.travelTimeBefore || 0,
      travelTimeAfter: appointment.travelTimeAfter || 0,
      startAddress: appointment.startAddress?.custom || '',
      endAddress: appointment.endAddress?.custom || '',
      isRecurring: isRecurring,
      // IMPORTANT: recurrencePattern must be null if not recurring (database constraint)
      recurrencePattern: isRecurring ? (appointment.recurrencePattern || null) : null,
      recurrenceEndDate: appointment.recurrenceEndDate || null,
      overrideDate: appointment.overrideDate || null,
      overrideParentId: appointment.overrideParentId || null,
      isProfessional: appointment.isProfessional || false
    };
  },

  /**
   * Get all appointments (optionally filtered by date range)
   */
  getAppointments: async (params?: { startDate?: string; endDate?: string }): Promise<PrivateAppointment[]> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = `/api/admin/mon-espace/appointments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<{ success: boolean; data: PrivateAppointmentDTO[] }>(url);

      if (response.success && response.data) {
        return response.data.map(monEspaceAPI.transformAppointmentFromDTO);
      }
      return [];
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  },

  /**
   * Create a new appointment
   */
  createAppointment: async (appointment: Partial<PrivateAppointment>): Promise<{ id: string }> => {
    try {
      const requestData = monEspaceAPI.transformAppointmentToRequest(appointment);
      const response = await apiClient.post<{ success: boolean; data: { id: string }; message: string }>(
        '/api/admin/mon-espace/appointments',
        requestData
      );

      if (response.success && response.data) {
        return { id: response.data.id };
      }
      throw new Error(response.message || 'Failed to create appointment');
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  /**
   * Update an existing appointment
   */
  updateAppointment: async (appointmentId: string, updates: Partial<PrivateAppointment>): Promise<{ success: boolean }> => {
    try {
      const requestData: UpdateAppointmentRequest = {
        date: updates.date,
        dayOfWeek: updates.dayOfWeek,
        startTime: updates.startTime,
        endTime: updates.endTime,
        title: updates.title,
        travelTimeBefore: updates.travelTimeBefore,
        travelTimeAfter: updates.travelTimeAfter,
        startAddress: updates.startAddress?.custom,
        endAddress: updates.endAddress?.custom,
        exceptions: updates.exceptions,
        isProfessional: updates.isProfessional
      };

      const response = await apiClient.put<{ success: boolean; message: string }>(
        `/api/admin/mon-espace/appointments/${appointmentId}`,
        requestData
      );

      return { success: response.success };
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  },

  /**
   * Delete an appointment
   */
  deleteAppointment: async (appointmentId: string): Promise<{ success: boolean }> => {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `/api/admin/mon-espace/appointments/${appointmentId}`
      );

      return { success: response.success };
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  }
};
